import { useEffect, useId, useState } from "react";
import { Mui, useTheme } from "@leap4/frontend";

const { Box, Paper, Skeleton, Typography } = Mui;

const STEPS = ["Reading your counted conversions", "Estimating what's still settling", "Forecasting the days ahead", "Almost there"];
const STEP_MS = 1400;

// A stylised series: settled history up to "today", then a forecast with a widening range.
const HISTORY = "M0,150 C40,140 70,120 110,126 S180,96 220,104 S300,80 360,86";
const FORECAST = "M360,86 C420,78 480,70 600,60";
const BAND = "M360,86 C420,60 480,40 600,22 L600,98 C480,100 420,96 360,86 Z";

const panelSx = { border: 1, borderColor: "divider", boxShadow: "none", borderRadius: "12px" };

/** Shown while the projection loads: the chart draws itself so the wait reads as work, not a blank page. */
export const ProjectingPlaceholder = ({ cards, chartHeight }: { cards: number; chartHeight: number }) => {
    const theme = useTheme();
    const id = useId().replace(/:/g, "");
    const [step, setStep] = useState(0);
    const color = theme.palette.primary.main;

    useEffect(() => {
        const timer = window.setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), STEP_MS);
        return () => window.clearInterval(timer);
    }, []);

    return (
        <Box aria-busy="true">
            <Box sx={{ display: "grid", gap: 1.25, mb: 2.5, gridTemplateColumns: { xs: "1fr", sm: `repeat(${Math.min(cards, 3)}, minmax(0, 1fr))` } }}>
                {Array.from({ length: cards }, (_, i) => (
                    <Skeleton key={i} variant="rectangular" animation="wave" height={180} sx={{ borderRadius: "12px" }} />
                ))}
            </Box>
            <Paper sx={{ ...panelSx, p: 2.5, height: chartHeight + 56, display: "flex", flexDirection: "column" }}>
                <Box sx={{ flex: 1, position: "relative", minHeight: 0 }}>
                    <svg width="100%" height="100%" viewBox="0 0 600 180" preserveAspectRatio="none" aria-hidden="true">
                        <style>{`
                            .pp-reveal-${id} { transform-box: fill-box; transform-origin: left; animation: pp-reveal-${id} 3.2s ease-out infinite; }
                            @keyframes pp-reveal-${id} { 0% { transform: scaleX(0); opacity: 1; } 70% { transform: scaleX(1); opacity: 1; } 90% { transform: scaleX(1); opacity: 1; } 100% { transform: scaleX(1); opacity: 0; } }
                            @media (prefers-reduced-motion: reduce) { .pp-reveal-${id} { animation: none; } }
                        `}</style>
                        <defs>
                            <clipPath id={`pp-clip-${id}`}>
                                <rect className={`pp-reveal-${id}`} x="0" y="0" width="600" height="180" />
                            </clipPath>
                        </defs>
                        {[30, 75, 120, 165].map((y) => (
                            <line key={y} x1="0" x2="600" y1={y} y2={y} stroke={theme.palette.divider} strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
                        ))}
                        <g clipPath={`url(#pp-clip-${id})`}>
                            <path d={BAND} fill={color} fillOpacity={0.14} />
                            <path d={HISTORY} fill="none" stroke={color} strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
                            <path d={FORECAST} fill="none" stroke={color} strokeWidth={2.5} strokeDasharray="6 5" vectorEffect="non-scaling-stroke" />
                            <line x1="360" x2="360" y1="10" y2="170" stroke={theme.palette.text.disabled} strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />
                        </g>
                    </svg>
                </Box>
                <Box role="status" sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, pt: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Projecting
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {STEPS[step]}…
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};
